import { observable } from 'mobx';
import type DeviceModel from 'app/stores/device/DeviceModel';
import { Model } from 'core/model/Model';
import { DeviceEventFields, DeviceEventKeys } from '@kentik/ui-shared/device/constants';
import { SimpleModel } from 'core/model/model.types';
import { PagedCollection } from 'core/model/PagedCollection';
import { ApiResponseWithJsonRoot } from 'core/model/Collection';

type DeviceSyslogsModel = SimpleModel<keyof typeof DeviceEventFields>;

export class DeviceSyslogsCollection extends PagedCollection<Model<DeviceSyslogsModel>> {
  @observable.ref
  device: DeviceModel;

  get model() {
    return Model<DeviceSyslogsModel>;
  }

  constructor(device: DeviceModel) {
    super();
    this.device = device;
    this.serverSortableFields = [
      DeviceEventKeys.timestamp,
      DeviceEventKeys.format,
      DeviceEventKeys.transport,
      DeviceEventKeys.dataset,
      DeviceEventKeys.severity,
      DeviceEventKeys.facility
    ];
    this.setPageState({ perPage: 20 });
    this.requestStatus = 'awaitingRequest';
  }

  get url() {
    return `/api/ui/devices/${this.device.id}/syslog/query`;
  }

  get fetchMethod() {
    return 'post' as const;
  }

  protected get jsonRoot() {
    return 'data';
  }

  protected get queuedFetchKey() {
    return `DeviceSyslogsCollection.${this.device.id}`;
  }

  protected get defaultSortState(): { field: typeof DeviceEventKeys.timestamp; direction: 'desc' } {
    return { field: DeviceEventKeys.timestamp, direction: 'desc' };
  }

  get sortParam(): { field?: string; direction?: 'asc' | 'desc' } {
    const { field, direction } = this.sortState;
    return { field, direction };
  }

  /**
   * Sorting is applied by the Table, which re-fetches via `pagedFetch` whenever `sortParam` changes
   * (including the filters, window, search and pagination). The base implementation would fire a second,
   * un-queued request with only `{ force, offset, sortBy }`, racing the real one and overwriting its results.
   */
  serverSortFn() {
    return Promise.resolve();
  }

  processData(response: ApiResponseWithJsonRoot<DeviceModel>) {
    const result = super.processData(response);
    this.setPageState({ pageCountLoaded: true });
    return result;
  }
}
